/**
 * Convert a hex color code string in the format #abcdef
 * to an array of RGB values in the [0,1] range (used by Paraview)
 * @param hex the hex color code to convert
 * @returns an array of 3 RGB values in the [0,1] range
 */
export function hexToRgbArray(hex: string): number[] {
	// Remove the hash (#) if it's there
	hex = hex.replace(/^#/, "");

	// Parse the hex color into r, g, b
	const rgbInt = parseInt(hex, 16);
	const r = (rgbInt >> 16) & 255;
	const g = (rgbInt >> 8) & 255;
	const b = rgbInt & 255;

	// Normalize to 0-1 range
	return [r / 255, g / 255, b / 255];
}

/**
 * Convert Paraview RGB [r, g, b] to hex #abcdef where {r, g, b} are in the [0-1] range
 * @param rgb array of 3 RGB values in the [0,1] range
 * @returns the hex code of the color, or null if no argument is provided
 * @throws if the input is not null, and is not an array of 3 numbers
 */
export function rgbArrayToHex(rgb: number[]): string | null {
	if (rgb == null || rgb.length == 0) {
		return null;
	}

	if (!Array.isArray(rgb) || rgb.length !== 3) {
		throw new Error(
			"Invalid RGB array. Expected format: [r, g, b] with values in [0,1] range."
		);
	}

	// Convert RGB values from [0,1] to [0,255] and format as a 2-digit hex
	return `#${rgb
		.map((i) =>
			Math.round(i * 255)
				.toString(16)
				.padStart(2, "0")
		)
		.join("")}`;
}

/**
 * Convert a hex color code string in the format #abcdef
 * to an object with r, g, b properties in the [0,255] range
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return {
		r: parseInt(result![1], 16),
		g: parseInt(result![2], 16),
		b: parseInt(result![3], 16)
	};
}

/**
 * Convert an RGB color {r, g, b} to a hex color code string
 */
export function rgbToHex(r: number, g: number, b: number): string {
	const componentToHex = (c: any) => {
		const hex = c.toString(16);
		return hex.length == 1 ? "0" + hex : hex;
	};
	return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}
